import * as React from "react";

import {
  ComboBox,
  DirectionalHint,
  EventGroup,
  find,
  ICalloutProps,
  IComboBox,
  IComboBoxOption,
  IComboBoxOptionStyles,
  Icon,
  IOnRenderComboBoxLabelProps,
  ISelectableOption,
  KeyCodes,
  Label,
  memoizeFunction,
  SelectableOptionMenuItemType,
  Stack,
  useKeytipRef,
} from "@fluentui/react";
import { ITheme } from "@fluentui/react/lib/Styling";

import { menuButtonIconStyles } from "@1js/acui-button";
import {
  dataUniqueId,
  generateAriaDescription,
  getCaretDownButtonStyles,
  getPreventMenuClickCallback,
  isIdleCallbackEnabled,
  preventNestedFunctionCalls,
} from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { focusableDiv, getMemoizedInputProps } from "@1js/acui-focusable-div";
import { getDownChevronIcon, iconNameToIconProps } from "@1js/acui-icons";
import { defaultKeytipExecute, deriveInputKeytip } from "@1js/acui-keytip";
import {
  computeNextShadowRenderingIncrement,
  getShadowRenderMenuValue,
} from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost, getTooltipText } from "@1js/acui-tooltip";
import { getBoundingWindowWithPaddingFabric } from "@1js/window-util";

import {
  AppComboBoxInternalProps,
  AppComboBoxItem,
  AppComboBoxOption,
} from "./AppComboBox.Props";
import {
  getComboBoxClassNames,
  getComboBoxIconClassNames,
  getComboBoxOptionClassNames,
  getComboBoxOptionStyles,
  getComboxLabelClassname,
  getControlContainerStyles,
  getTextContainerClassname,
} from "./AppComboBox.styles";

// TODO: Update/Remove this as part of VSTS: 2463469 - ComboBox: Move "isValidValue" functionality closer to the component
export interface AppComboBoxState {
  option?: ISelectableOption;
  text?: string;
  prevPropsSelectedKey?: string | null;
  prevPropsValue?: string;
  // Whether the menu has been opened at least once after mounting
  menuHasBeenOpened?: boolean;
  itemsToRender?: number;
  isShadowRendering?: boolean;
}

export class AppComboBoxBase extends React.PureComponent<
  AppComboBoxInternalProps,
  AppComboBoxState
> {
  private keydown: string = "keydown";
  private enterKey: string = "Enter";
  private comboBox = React.createRef<IComboBox>();
  private tooltipHost: AppTooltipHost | undefined;
  private tooltipHostMap: Map<string, AppTooltipHost> = new Map();

  // VSTS: 3652261 - ComboBox: ESC is not cancelling a pending value and is instead submitting the value
  // Member variables needed to restore the original value
  // Reference: https://office.visualstudio.com/OC/_wiki/wikis/OC.wiki/9723/Event-propagation-mixing-React-Synthetic-Events-and-native-events
  private appComboBoxWrapper = React.createRef<HTMLDivElement>(); // wrapper to handle native onkeydown event
  private events: EventGroup; // Fabric's class to handle native eventing
  private processingEscKeyDown: boolean = false;
  private initialText: string | undefined;
  private didSetInitialText: boolean = false;
  private initiatedShadowRendering = false;

  private currentShadowRenderingStart: number | undefined;
  private totalShadowRenderingTime: number = 0;

  // We will default to a generic ID and set a specific Id with the
  // custom combobox Id when we render
  private comboBoxDropdownId: string = "ComboBoxDropDownId";

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public static defaultProps: Partial<AppComboBoxInternalProps> = {
    autoComplete: true,
  };

  constructor(props: AppComboBoxInternalProps) {
    super(props);

    this.state = {
      prevPropsSelectedKey: props.selectedKey,
      prevPropsValue: props.value,
      menuHasBeenOpened: false,
      isShadowRendering: false,
    };

    this.events = new EventGroup(this);
  }

  public render() {
    let initialValue = {};

    // If we're in an intermediary state where we are waiting for the RibbonStore to update our ComboBox values, we would
    // use the state that we set while we're waiting for the updated values.
    if (this.state.text) {
      initialValue = { text: this.state.text };
    } else if (this.props.selectedKey === undefined && this.state.option) {
      initialValue = { selectedKey: this.state.option.key };
    } else if (this.props.value && this.props.selectedKey !== undefined) {
      initialValue = {
        text: this.props.value,
        selectedKey: this.props.selectedKey,
      };
    } else {
      // ComboBox emits a warning for setting both initial value and defaultSelectedKey, even if one of them is set to undefined.
      // We should be sure to only set one of these values.
      initialValue = this.props.value
        ? { text: this.props.value }
        : this.props.selectedKey !== undefined
        ? { selectedKey: this.props.selectedKey }
        : { defaultSelectedKey: this.props.defaultSelectedKey };
    }

    const {
      disabled,
      allowFreeForm,
      autoComplete,
      options,
      label,
      showLabel,
      dataNav,
      preventMenuExecution,
      placeholder,
      useComboBoxAsMenuWidth,
    } = this.props;
    const { isShadowRendering } = this.state;

    // ComboBoxes with headers will style their options with extra padding/indentation
    const containsHeader = options.some((option) => {
      return option.type === "AppComboBoxHeader";
    });
    // ComboBoxes with icons will style their options with extra padding/indentation
    const containsIcons = options.some((option) => {
      return option.type === "AppComboBoxItem" && !!option.data;
    });
    const { ariaDescriptionId, ariaDescriptionElement } =
      generateAriaDescription(this.props.id, this.props.customTooltip);

    const comboboxId = dataUniqueId(this.props) || this.props.id;
    this.comboBoxDropdownId = comboboxId + "Dropdown";

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <div
            ref={this.appComboBoxWrapper}
            className={getControlContainerStyles(
              this.props.styles && this.props.styles.controlContainer,
              this.props.identifiersAbove
            )}
          >
            <ComboBox
              {...initialValue}
              ref={useKeytipRef<HTMLDivElement>({
                keytipProps: deriveInputKeytip(
                  this.props.keytipProps,
                  this.keytipExecute
                ),
              })}
              componentRef={this.comboBox}
              disabled={disabled}
              allowFreeform={allowFreeForm}
              autoComplete={autoComplete ? "on" : "off"}
              options={this.getOptionsToRender(
                theme,
                containsHeader || containsIcons
              )}
              onChange={this.onExecute}
              onItemClick={this.onItemClick}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onMenuOpen={this.onMenuOpen}
              onMenuDismissed={this.onAfterMenuDismiss}
              onPendingValueChanged={this.onPreviewExecute}
              onInputValueChange={this.props.onInputValueChange}
              getClassNames={getComboBoxClassNames(
                this.props.styles,
                this.props.width,
                this.props.pinToPreviousComboBox,
                this.props.identifiersAbove
              )}
              caretDownButtonStyles={getCaretDownButtonStyles(
                theme,
                this.props.caretDownButtonStyles
              )}
              buttonIconProps={iconNameToIconProps(
                getDownChevronIcon(this.props)
              )}
              label={showLabel ? label : undefined}
              data-automation-type={
                this.props.dataAutomationType || "AppComboBox"
              }
              data-unique-id={dataUniqueId(this.props)}
              title={undefined}
              aria-posinset={this.props.positionInSet}
              aria-setsize={this.props.setSize}
              ariaLabel={label}
              ariaDescribedBy={ariaDescriptionElement && ariaDescriptionId}
              calloutProps={this.getCalloutProps(
                this.comboBoxDropdownId,
                !!isShadowRendering,
                !!this.props.preventCalloutDismissOnScroll
              )}
              onRenderOption={this.onRenderOption(theme)}
              persistMenu={this.shouldPersistMenu()}
              autofill={getMemoizedInputProps(this.context, dataNav)}
              onRenderLabel={this.getOnRenderLabel(theme)}
              id={comboboxId}
              placeholder={placeholder}
              useComboBoxAsMenuWidth={useComboBoxAsMenuWidth}
              iconButtonProps={
                preventMenuExecution
                  ? { onClick: getPreventMenuClickCallback() }
                  : undefined
              }
            />
            {ariaDescriptionElement}
          </div>
        )}
      </AppThemeConsumer>
    );
  }

  public componentDidMount() {
    if (
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      !this.props.disableTooltips
    ) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.comboBox,
        controlId: this.props.id,
        controlLabel: getTooltipText(
          this.props.showLabel,
          this.props.label,
          this.props.customTooltip
        ),
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(
          this.context,
          ribbonExperiments.delayRenderTooltipsIsEnabled
        ),
      });
      this.tooltipHost.registerTooltipHostActions();
    }

    this.tryInitiateShadowRendering();

    if (this.appComboBoxWrapper.current) {
      // VSTS: 3652261 - ComboBox: ESC is not cancelling a pending value and is instead submitting the value
      // Handle keydown on the browser's capture phase so it can decide if it needs to reset the value before
      // the component loses focus.
      this.events.on(
        this.appComboBoxWrapper.current,
        this.keydown,
        this.onKeyDown,
        true /* useCapture */
      );
    }
  }

  /**
   * This method is called on componentDidMount and componentDidUpdate.
   * If shadow rendering has already been initiated this is no-op.
   * If not, it tries to initiate it.
   * The reason why we have to call this multiple times on update is because
   * we do not want to initiate shadow rendering when the control is disabled.
   */
  private tryInitiateShadowRendering = () => {
    if (
      !this.initiatedShadowRendering &&
      getShadowRenderMenuValue(this.props.shadowRenderMenu) &&
      isIdleCallbackEnabled() &&
      !this.props.disabled &&
      this.state.menuHasBeenOpened === false
    ) {
      this.initiatedShadowRendering = true;
      window.requestIdleCallback(
        () => {
          const { id, location } = this.props;
          if (this.props.onMenuOpen) {
            this.props.onMenuOpen({ id, location, isShadowRendering: true });
          }
        },
        undefined /*options*/,
        `ComboBox-${this.props.id}-ShadowMenuOpen`
      );

      window.requestIdleCallback(
        () => {
          if (this.state.menuHasBeenOpened === false) {
            this.setState({
              isShadowRendering: true,
              menuHasBeenOpened: true,
              itemsToRender: 0,
            });
          }
        },
        undefined /*options*/,
        `ComboBox-${this.props.id}-StartShadowRendering`
      );
    }
  };

  public componentDidUpdate(prevProps: AppComboBoxInternalProps) {
    this.tryInitiateShadowRendering();
    this.renderMoreItemsInIdleCallback();
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip ||
        prevProps.label !== this.props.label ||
        prevProps.showLabel !== this.props.showLabel)
    ) {
      this.tooltipHost.updateTooltip(
        getTooltipText(
          this.props.showLabel,
          this.props.label,
          this.props.customTooltip
        )
      );
    }
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }

    // Remove the event handlers that added in componentDidMount
    this.events.off();
  }

  public static getDerivedStateFromProps(
    nextProps: AppComboBoxInternalProps,
    state: AppComboBoxState
  ) {
    // There's a gap between when we update the ComboBox value with the new selected value where
    // the prop still has the old value, causing a flicker in the ComboBox where we switch from old to new value
    // To fix this, we set a state that will show the value we submitted until we get the new prop and remove the
    // state value.
    if (
      state.prevPropsSelectedKey !== nextProps.selectedKey ||
      state.prevPropsValue !== nextProps.value
    ) {
      return {
        prevPropsSelectedKey: nextProps.selectedKey,
        prevPropsValue: nextProps.value,
        option: undefined,
        text: "",
      };
    }
    return null;
  }

  private getCalloutProps = memoizeFunction(
    (
      comboBoxDropdownId: string,
      isShadowRendering: boolean,
      preventCalloutDismissOnScroll: boolean
    ): ICalloutProps => {
      return {
        directionalHintFixed: !this.props.directionalHintDynamic,
        id: comboBoxDropdownId,
        onPositioned: this.onCalloutReady,
        bounds: getBoundingWindowWithPaddingFabric,
        shouldUpdateWhenHidden: isShadowRendering, // This helps shadow rendering to incrementally mount menu items when hidden.
        preventDismissOnScroll: preventCalloutDismissOnScroll,
      };
    }
  );

  private getOnRenderLabel = memoizeFunction((theme: ITheme) => {
    return (comboBoxLabelRenderProps: IOnRenderComboBoxLabelProps) => {
      const {
        iconStyles,
        label,
        showLabel,
        styles,
        identifiersAbove,
        icon,
        iconColor,
        disabled,
      } = this.props;
      if (!showLabel && !icon) {
        return null;
      }

      const iconClassNames = getComboBoxIconClassNames(
        showLabel,
        styles,
        iconStyles,
        identifiersAbove,
        disabled
      );
      const { required } = comboBoxLabelRenderProps.props;
      const id = dataUniqueId(this.props) || this.props.id;

      const labelContents = (
        <div className={getTextContainerClassname(styles?.textContainer)}>
          <Label
            htmlFor={id + "-input"} // Fabric appends '-input' to the Id
            id={id + "-label"}
            className={getComboxLabelClassname({
              theme,
              customStyles: styles,
              identifiersAbove,
              disabled,
            })}
            disabled={disabled}
            required={required}
          >
            {label}
          </Label>
        </div>
      );

      return this.props.icon ? (
        <div className={iconClassNames.controlAndIconContainer}>
          <Icon
            {...iconNameToIconProps(icon, iconColor)}
            className={iconClassNames.icon}
          />
          {showLabel && labelContents}
        </div>
      ) : (
        labelContents
      );
    };
  });

  /**
   * In shadow rendering mode, we might only want to render some of the comboBox options
   * This method takes care of that.
   */
  private getOptionsSliceToRender = () => {
    if (this.state.isShadowRendering) {
      return this.props.options.slice(0, this.state.itemsToRender);
    }

    return this.props.options;
  };

  private getOptionsToRender = (theme: ITheme, shouldAddPadding: boolean) => {
    const optionsToRender = this.getOptionsSliceToRender();
    return this.getComboBoxOptionsToRender(
      theme,
      optionsToRender,
      shouldAddPadding,
      this.props.optionStyles
    );
  };

  private getComboBoxOptionsToRender = memoizeFunction(
    (
      theme: ITheme,
      options: AppComboBoxOption[],
      shouldAddPadding: boolean,
      optionStyles?: Partial<IComboBoxOptionStyles>
    ) => {
      return options.map((option) =>
        comboBoxOptionToProps(theme, option, optionStyles, shouldAddPadding)
      );
    }
  );

  /**
   * This method increments the number of items to render in shadow mode. And these new items are mounted
   * in an idle callback.
   */
  private renderMoreItemsInIdleCallback = () => {
    const { isShadowRendering, itemsToRender } = this.state;
    const { options } = this.props;
    if (isShadowRendering && itemsToRender !== undefined) {
      if (itemsToRender < options.length) {
        if (this.currentShadowRenderingStart) {
          this.totalShadowRenderingTime +=
            performance.now() - this.currentShadowRenderingStart;
        }

        const nextIncrement = computeNextShadowRenderingIncrement(
          this.totalShadowRenderingTime,
          itemsToRender
        );
        isIdleCallbackEnabled() &&
          window.requestIdleCallback(
            () => {
              this.currentShadowRenderingStart = performance.now();
              // This state update is little tricky. Normally, for counters, we need to use the functional version
              // to not miss state updates. But here, we prefer to use this because we do not care if we miss
              // a few updates, but we do not want two state updates to combined in one render.
              this.setState({
                itemsToRender: itemsToRender! + nextIncrement,
              });
            },
            undefined /*options*/,
            `ComboBox-${this.props.id}-ShadowRenderIncrement`
          );
      } else {
        this.setState({
          isShadowRendering: false,
        });
      }
    }
  };

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget);
  };

  // The onExecute function in the office-ui-fabric-react combo box can be executed more than once if the onExecute handler
  // causes the combo box to lose focus. An example of this is the Word canvas taking focus from the combo box as a result
  // of applying the style.
  private onExecute = preventNestedFunctionCalls(
    (
      submitPendingValueEvent?: React.FormEvent<IComboBox>,
      option?: ISelectableOption,
      index?: number,
      text?: string
    ) => {
      if (!this.props.onExecute) return;

      // VSTS: 3652261 - ComboBox: ESC is not cancelling a pending value and is instead submitting the value
      // If the user has pressed the Esc key, we shouldn't submit calls to the command handler
      // that updates the combo box value. We should reset the text instead.
      if (this.processingEscKeyDown || this.didSetInitialText) {
        if (!this.didSetInitialText) {
          this.processingEscKeyDown = false;
          if (this.initialText !== undefined) {
            this.setState({ text: this.initialText });
            this.initialText = undefined;
          }
        }
        return;
      }

      // If the combo box is controlled, we shouldn't use setState to eagerly apply the new selection
      const isControlledComboBox = this.props.selectedKey !== undefined;

      let shouldTakeFocus = true;
      if (this.props.shouldTakeFocus !== undefined) {
        shouldTakeFocus = this.props.shouldTakeFocus;
      } else if (
        submitPendingValueEvent &&
        submitPendingValueEvent.type === this.keydown
      ) {
        shouldTakeFocus =
          (submitPendingValueEvent.nativeEvent as KeyboardEvent).which !==
          KeyCodes.tab;
      }

      if (text) {
        if (!isControlledComboBox) {
          this.setState({ text });
        }

        if (
          !this.props.executeOnlyOnEnterKey ||
          (submitPendingValueEvent?.nativeEvent as KeyboardEvent)?.key ===
            this.enterKey
        ) {
          this.props.onExecute({
            id: this.props.id,
            newValue: text,
            location: this.props.location,
            shouldTakeFocus,
          });
        }
      } else if (
        option &&
        index !== undefined &&
        typeof option.key === "string"
      ) {
        if (!isControlledComboBox) {
          this.setState({ option });
        }

        const optionWithCommandValueId: AppComboBoxItem =
          this.props.options.filter(
            (element) =>
              element.key === option.key && element.type === "AppComboBoxItem"
          )[0] as AppComboBoxItem;

        this.props.onExecute({
          id: this.props.id,
          newValue: {
            key: option.key,
            text: option.text,
            index,
            commandValueId:
              optionWithCommandValueId &&
              optionWithCommandValueId.commandValueId,
          },
          location: this.props.location,
          shouldTakeFocus,
        });
      }

      // VSTS: 3652261 - ComboBox: ESC is not cancelling a pending value and is instead submitting the value
      // If we submitted a new value, the next time we change the value, we should save it again.
      this.didSetInitialText = false;
    }
  );

  // The onItemClick function in the office-ui-fabric-react combo box can be executed more than once if the onItemClick handler
  // causes the combo box to lose focus. An example of this is the Word canvas taking focus from the combo box as a result
  // of applying the style.
  private onItemClick = preventNestedFunctionCalls(
    (
      submitPendingValueEvent?: React.FormEvent<IComboBox>,
      option?: ISelectableOption,
      index?: number
    ) => {
      if (!this.props.onItemClick) return;

      let shouldTakeFocus = true;
      if (this.props.shouldTakeFocus !== undefined) {
        shouldTakeFocus = this.props.shouldTakeFocus;
      } else if (
        submitPendingValueEvent &&
        submitPendingValueEvent.type === this.keydown
      ) {
        shouldTakeFocus =
          (submitPendingValueEvent.nativeEvent as KeyboardEvent).which !==
          KeyCodes.tab;
      }

      if (option && index !== undefined && typeof option.key === "string") {
        const optionWithCommandValueId: AppComboBoxItem =
          this.props.options.filter(
            (element) =>
              element.key === option.key && element.type === "AppComboBoxItem"
          )[0] as AppComboBoxItem;

        this.props.onItemClick({
          id: this.props.id,
          newValue: {
            key: option.key,
            text: option.text,
            index,
            commandValueId:
              optionWithCommandValueId &&
              optionWithCommandValueId.commandValueId,
          },
          location: this.props.location,
          shouldTakeFocus,
        });
      }
    }
  );

  private onBlur = () => {
    if (this.processingEscKeyDown) {
      this.processingEscKeyDown = false;
      if (this.initialText !== undefined) {
        this.setState({ text: this.initialText });
        this.initialText = undefined;
        this.didSetInitialText = true;
      }
    }
  };

  private onFocus = () => {
    // we only need to do this if we haven't gotten any options yet
    // (e.g. this is for populating the comboBox for autocomplete before the menu is expanded)
    if (this.props.options.length === 0 && this.props.onFocus) {
      this.props.onFocus();
    }

    // VSTS: 3652261 - ComboBox: ESC is not cancelling a pending value and is instead submitting the value
    // Store the value that we need to reset in case the user attempts to revert submission
    if (this.props.selectedKey) {
      this.didSetInitialText = false;
      // Find the text based on the selected key
      const selectedOption = find(
        this.props.options,
        (option) =>
          option.type === "AppComboBoxItem" &&
          option.key === this.props.selectedKey
      ) as AppComboBoxItem;
      this.initialText = selectedOption.text;
    }
  };

  private onMenuOpen = () => {
    if (!this.state.menuHasBeenOpened) {
      this.setState({
        menuHasBeenOpened: true,
      });
    }

    this.resetShadowRenderingAfterUserAction();

    if (this.props.onMenuOpen) {
      const { id, location } = this.props;
      this.props.onMenuOpen({ id, location });
    }
    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(true);
    }
  };

  private onAfterMenuDismiss = () => {
    if (this.props.onAfterMenuDismiss) {
      this.props.onAfterMenuDismiss(this.props.id);
    }
    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(false);
    }
    if (this.tooltipHostMap) {
      this.unregisterTooltips();
    }
  };

  private unregisterTooltips() {
    this.tooltipHostMap.forEach((tooltipHost) => {
      tooltipHost.unregisterTooltipHostActions();
    });
    this.tooltipHostMap.clear();
  }

  private onCalloutReady = () => {
    if (
      this.tooltipHost &&
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      this.tooltipHostMap.size === 0
    ) {
      const htmlItems = this.createArrayOfButtonElements();
      if (
        this.props.disableTooltips ||
        this.applyTooltipsToComboBoxItems(htmlItems)
      ) {
        this.removeBrowserTooltips(htmlItems);
      }
    }
  };

  private showTooltipForComboboxItem = (optionKey: string): void => {
    // First check the size of the tooltipHostMap. If it is 0, then
    // fancy tooltips may not be enabled or the menu is collapsed
    // and we can bail out.
    if (this.tooltipHostMap.size > 0) {
      const tooltipHostForElement = this.tooltipHostMap.get(optionKey);
      if (tooltipHostForElement) {
        tooltipHostForElement.setTooltip();
      }
    }
  };

  private removeBrowserTooltips(htmlItems: HTMLElement[]): void {
    htmlItems.forEach((element: HTMLElement) => {
      element.setAttribute("title", "");
    });
  }

  private createArrayOfButtonElements(): HTMLElement[] {
    const items: HTMLElement[] = [];

    const menu = document.getElementById(this.comboBoxDropdownId);
    if (menu !== null) {
      const childItems = menu.querySelectorAll("button");
      childItems.forEach((item: HTMLElement) => {
        items.push(item);
      });
    }

    return items;
  }

  private createArrayOfAppComboBoxItems(): AppComboBoxItem[] {
    const items: AppComboBoxItem[] = [];

    this.props.options.forEach((option: AppComboBoxOption) => {
      if (option.type === "AppComboBoxItem") {
        items.push(option);
      }
    });

    return items;
  }

  private applyTooltipsToComboBoxItems = (items: HTMLElement[]): boolean => {
    const appControlProps = this.createArrayOfAppComboBoxItems();
    if (items.length !== appControlProps.length) {
      return false;
    }

    appControlProps.forEach(
      (comboBoxItemProps: AppComboBoxItem, index: number) => {
        if (
          comboBoxItemProps.ariaLabel === undefined ||
          comboBoxItemProps.ariaLabel.length === 0 ||
          comboBoxItemProps.ariaLabel === comboBoxItemProps.text
        ) {
          // Do not show tooltips if it has exactly same content as the options.
          return;
        }
        const element = items[index];
        if (
          this.context.updateTooltipsHidden &&
          this.context.setTooltipElement
        ) {
          const tooltipHost = new AppTooltipHost({
            updateTooltipsHidden: this.context.updateTooltipsHidden,
            setTooltipElement: this.context.setTooltipElement,
            htmlElement: element,
            controlId: comboBoxItemProps.key,
            controlLabel: comboBoxItemProps.ariaLabel,
            tooltipTopMargin: 0,
            isMenuComponent: true,
            tooltipDirection:
              this.props.tooltipDirection || DirectionalHint.leftCenter,
          });
          tooltipHost.registerTooltipHostActions();
          this.tooltipHostMap.set(comboBoxItemProps.key, tooltipHost);
        }
      }
    );

    return true;
  };

  private onPreviewExecute = (
    option?: IComboBoxOption,
    index?: number,
    text?: string
  ) => {
    if (option !== undefined || index !== undefined || text !== undefined) {
      if (this.props.onPreviewExecute && text && text !== "") {
        this.props.onPreviewExecute({ id: this.props.id, newValue: text });
      } else if (
        this.props.onPreviewExecute &&
        index !== undefined &&
        index >= 0 &&
        option &&
        typeof option.key === "string"
      ) {
        const optionWithCommandValueId: AppComboBoxItem =
          this.props.options.filter(
            (element) =>
              element.key === option.key && element.type === "AppComboBoxItem"
          )[0] as AppComboBoxItem;

        this.props.onPreviewExecute({
          id: this.props.id,
          newValue: {
            key: option.key,
            text: option.text,
            index,
            commandValueId:
              optionWithCommandValueId &&
              optionWithCommandValueId.commandValueId,
          },
        });

        this.showTooltipForComboboxItem(option.key);
      }
    } else if (this.props.onRevertPreviewExecute) {
      this.props.onRevertPreviewExecute();
    }
  };

  private onRenderOption = (theme: ITheme) => {
    return (item: IComboBoxOption) => {
      const optionClassNames = getComboBoxOptionClassNames(
        getComboBoxOptionStyles(theme, this.props.optionStyles, item.styles)
      );
      return (
        <Stack horizontal={true} className={optionClassNames.stackStyle}>
          {item.data?.icon && (
            <Stack.Item align="center">
              <Icon
                {...iconNameToIconProps(item.data.icon)}
                styles={{
                  root: menuButtonIconStyles(theme),
                }}
              />
            </Stack.Item>
          )}
          <Stack className={optionClassNames.stackStyle}>
            <span
              className={`${optionClassNames.optionText} AppComboBox-OptionText`}
            >
              {item.text}
            </span>
            {item.data?.subText && (
              <span className={optionClassNames.subTextStyle}>
                {item.data.subText}
              </span>
            )}
          </Stack>
        </Stack>
      );
    };
  };

  /**
   * Handle keydown on the on AppComboBox.
   * This will get fired as part of the browser events rather than the React events.
   */
  private onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.which === KeyCodes.escape) {
      this.processingEscKeyDown = true;
    }
  };

  /**
   * Even if persistMenu is true, we only send that to the Fabric component after the menu has been opened
   * for the first time. Otherwise, once a menu is populated, tab switches start to be slow because each tab
   * when switched to would try to mount the whole menu whose options have already been populated.
   */
  private shouldPersistMenu(): boolean | undefined {
    return this.props.persistMenu && this.state.menuHasBeenOpened;
  }

  /**
   * If a user opens the menu by clicking, keyboarding, through keytips
   * or through the API, the first thing we need to do is set shadow rendering
   * to false.
   */
  private resetShadowRenderingAfterUserAction = () => {
    if (this.state.isShadowRendering) {
      this.setState({
        isShadowRendering: false,
      });
    }
  };
}

function comboBoxOptionToProps(
  theme: ITheme,
  option: AppComboBoxOption,
  optionStyles: Partial<IComboBoxOptionStyles> | undefined,
  dropdownHasHeaderOrIcons: boolean
): IComboBoxOption {
  switch (option.type) {
    case "AppComboBoxItem":
      return {
        itemType: SelectableOptionMenuItemType.Normal,
        key: option.key,
        text: option.text,
        styles: getComboBoxOptionStyles(
          theme,
          optionStyles,
          option.styles,
          dropdownHasHeaderOrIcons,
          !!option.data,
          !!option.data?.subText
        ) as IComboBoxOptionStyles,
        useAriaLabelAsText: option.ariaLabel != null,
        ariaLabel:
          option.ariaLabel ??
          `${option.text}${
            option.data?.subText ? "\n" + option.data.subText : ""
          }`,
        data: option.data,
      };
    case "AppComboBoxHeader":
      return {
        itemType: SelectableOptionMenuItemType.Header,
        key: option.key,
        text: option.text,
      };
    case "AppComboBoxDivider":
      return {
        itemType: SelectableOptionMenuItemType.Divider,
        key: option.key,
        text: "-",
      };
  }
}

export const AppComboBox = focusableDiv<
  AppComboBoxInternalProps,
  AppComboBoxState
>(AppComboBoxBase);
